import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Play, Bell, Search, LineChart, Key, Check, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from './Modal';

const slides = [
    {
      title: "Welcome to GitGraber",
      description: "Your advanced security solution for detecting and monitoring sensitive data leaks across public GitHub repositories in real-time.",
      icon: Shield
    },
    {
      title: "Configure Your Runners",
      description: "Set up and manage runners to continuously scan public repositories. Customize scanning patterns and schedules to match your security needs.",
      icon: Play
    },
    {
      title: "Real-time Notifications", 
      description: "Stay informed with instant alerts when sensitive data is detected. Receive notifications through the dashboard, Slack, or Discord.",
      icon: Bell
    },
    {
      title: "Advanced Token Detection",
      description: "Use our powerful pattern matching system to detect API keys, passwords, tokens and other sensitive data with customizable detection rules.",
      icon: Search
    },
    {
      title: "Security Analytics",
      description: "Track your security posture with comprehensive analytics. Monitor trends, identify vulnerabilities, and measure the effectiveness of your security measures.",
      icon: LineChart
    },
    {
      title: "Action Items",
      description: "Track and manage action items generated from findings. Prioritize, assign, and close action items to ensure timely resolution.",
      icon: Check
    },
    {
      title: "Team Management",
      description: "Manage your team's access and permissions. Add, remove, and manage user roles to ensure the right people have the right access.",
      icon: Users
    },
    {
      title: "Personal Access Token",
      description: "Generate a PAT to authenticate your requests to the GitGraber API. Each token can be granted with specific permissions.",
      icon: Key
    }
];
  
const OnboardingModal = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      title=""
      size="xl"
      className="!p-0"
      showCloseButton={false}
    >
      <div className="bg-surface-secondary p-8 md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center"
          >
            <motion.div
              className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-accent-soft rounded-2xl"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              {React.createElement(slides[currentSlide].icon, { 
                size: 48, 
                className: "text-accent" 
              })}
            </motion.div>
            <h2 className="text-headline text-ink mb-4">
              {slides[currentSlide].title}
            </h2>
            <p className="text-body text-ink-secondary mb-8 max-w-2xl mx-auto">
              {slides[currentSlide].description}
            </p>
          </motion.div>
        </AnimatePresence>
  
        <div className="flex justify-center space-x-2 mb-8">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-base ${
                index === currentSlide 
                  ? 'bg-accent w-8' 
                  : 'bg-surface-tertiary hover:bg-border w-2'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
  
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
            disabled={currentSlide === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-base ${
              currentSlide === 0 
                ? 'invisible' 
                : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
            }`}
          >
            <ChevronLeft size={18} />
            <span className="text-caption font-medium">Previous</span>
          </button>
          
          <div className="text-caption text-ink-tertiary">
            {currentSlide + 1} / {slides.length}
          </div>
          
          <button
            onClick={() => {
              if (currentSlide === slides.length - 1) {
                onComplete();
              } else {
                setCurrentSlide(prev => prev + 1);
              }
            }}
            className="flex items-center gap-2 px-6 py-2 bg-accent hover:bg-accent-hover text-white text-caption font-medium rounded-md transition-base"
          >
            <span>{currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}</span>
            {currentSlide < slides.length - 1 && <ChevronRight size={18} />}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OnboardingModal;